'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dynamic from 'next/dynamic';
import { useBoardStageContext, type BoardStagePhase } from './board-stage-context';
import {
  BUYOUT_TILE_VISIBLE_AT,
  BUYOUT_TOTAL_MS,
  CLAIM_TOTAL_MS,
  FORFEIT_DRAMATIC_MS,
} from '../board-canvas/animation-timings';
import {
  ensureHqAudioReady,
  hqPlayBuyout,
  hqPlayBuyoutConfirm,
  hqPlayClaim,
} from '../audio/hq-sounds';
import type {
  TileAnimState,
  TileHolderSnapshot,
} from '../board-canvas/TileEffectsOverlay';
import type { TileAnimKind } from '../panels/tile-anim-demo.types';
import { buildAnimState } from '../panels/tile-anim-demo.types';
import { CanvasErrorBoundary } from '../board-canvas/CanvasErrorBoundary';
import { useLobbyGridPage } from '@/hooks/use-lobby-grid-page';
import { useLobbySeats } from '../data/use-lobby-seats';
import { useHolderPlinths } from '../board-canvas/use-holder-plinths';
import { shouldForceTwoD } from '../settings/graphics-settings';
import type { GraphicsConfig } from '../settings/graphics-settings';
import type { SeatData } from '@/components/grid';
import type { SkyboxTheme } from '../theming/skyboxes/types';
import type { PlinthMaterialId } from '../theming/plinths/types';
import { sortedSeatsForBoard, type BoardSortMode } from '../board-canvas/tile-layout';
import type { BoardSeatTooltipContext } from '../board-canvas/board-seat-tooltip';
import type { BulkMode } from '../panels/basket/use-lobby-basket';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';
import { V2_CONSTANTS } from '@/lib/types/v2';
import { isV22Grid } from '@/lib/constants/addresses';
import { useWallet } from '@/hooks/use-wallet';
import { recordBootPhase } from '@/lib/boot-milestones';

/**
 * Lobby canvases are dynamically imported so the heavy R3F bundle is
 * not part of the initial lobby chunk. The same pattern existed in
 * `LobbyClient.tsx`; it's hoisted here because the slot is now the
 * canvas mount point.
 */
const BoardCanvas3D = dynamic(
  () => import('../board-canvas/BoardCanvas3D').then((m) => m.BoardCanvas3D),
  { ssr: false, loading: () => null },
);
const BoardCanvas2D = dynamic(
  () => import('../board-canvas/BoardCanvas2D').then((m) => m.BoardCanvas2D),
  { ssr: false, loading: () => null },
);

/**
 * Render-time props the slot needs from `LobbyClient` to draw its
 * canvas. Everything that's not `boardId` flows through unchanged
 * from the previous direct `<BoardCanvas3D / 2D>` mount.
 *
 * `boardId` is what makes a slot a slot: the data fetches +
 * per-tile state below all key off it, and a slot's `boardId` is
 * pinned once at mount (the stage replaces the slot entirely on a
 * board change rather than mutating the prop).
 */
export interface BoardStageSlotProps {
  /** Identity of the board this slot is rendering. Set once per
   *  slot lifetime; the stage swaps slots on board change. */
  boardId: string;
  /** Initial / preferred grid size for the board. The slot's own
   *  `useLobbyGridPage` may resolve a different value once the
   *  per-grid metadata lands; `seats.length` derived inside the
   *  slot is the authoritative count. */
  gridSize: number;
  /** Skybox theme (registry id) or `null` for the wizard's "None" pick. */
  skybox: SkyboxTheme | null;
  /** Resolved board-level plinth (registered material id, `null` for
   *  explicit None, `undefined` to fall back to the skybox default). */
  boardPlinth?: PlinthMaterialId | null;
  /** Whether this slot owns the URL-active board. The slot only
   *  installs its animation API into the stage context when this is
   *  true. Drives drawer-driven animation routing during the
   *  crossfade window. */
  isActiveTarget: boolean;
  /** Lifecycle phase. Drives `data-stage-phase`, `pointer-events`,
   *  and the canvas's `interactionsLocked` flag. */
  phase: BoardStagePhase;
  /** Fired exactly once after the canvas has painted its first
   *  frame AND the slot's data + forfeit baseline are ready. The
   *  stage gates the visible crossfade on this signal. */
  onReady?: () => void;
  /** Tutorial mode collapses the readiness gate so the scripted
   *  step doesn't have to wait for indexer data that won't arrive. */
  bypassReadinessGate?: boolean;
  graphics: GraphicsConfig;
  reducedMotion: boolean;
  isMobile: boolean;
  selectedIndices?: Set<number>;
  /** Active basket category — when non-null, every tile whose
   *  category doesn't match is dimmed by the canvases below. */
  bulkMode?: BulkMode | null;
  sortMode?: BoardSortMode;
  seatTooltip?: BoardSeatTooltipContext;
  stickyTooltipIndex?: number | null;
  onClearStickyTooltip?: () => void;
  onTileClick?: (seat: SeatData, opts: { toggleBasket: boolean }) => void;
  drawerOverlayOpen?: boolean;
  devRandomPedestals?: boolean;
}

/**
 * One self-contained board renderer.
 *
 * Each slot fetches its own data, owns its own per-tile state
 * (animations, holder overrides, forfeit baseline) and mounts its
 * own canvas (3D or 2D). Two slots can coexist briefly during a
 * crossfade — they don't share any state, so the outgoing slot's
 * fade can't bleed into the incoming slot's first paint.
 *
 * The stage routes drawer-/tutorial-/dev-driven animations to
 * whichever slot has `isActiveTarget=true` (the URL board).
 */
export function BoardStageSlot(props: BoardStageSlotProps) {
  const {
    boardId,
    gridSize,
    skybox,
    boardPlinth,
    isActiveTarget,
    phase,
    onReady,
    bypassReadinessGate = false,
    graphics,
    reducedMotion,
    isMobile,
    selectedIndices,
    bulkMode = null,
    sortMode = 'floor',
    seatTooltip,
    stickyTooltipIndex,
    onClearStickyTooltip,
    onTileClick,
    drawerOverlayOpen,
    devRandomPedestals,
  } = props;

  const stage = useBoardStageContext();
  const tutorial = useTutorialOptional();
  const { address: viewerAddress } = useWallet();

  // Per-slot data hooks. React Query dedupes when both slots happen
  // to share a boardId (cold mount path), so the second slot pays
  // no network cost — only the per-tile state below stays separated.
  const gridPage = useLobbyGridPage(boardId);
  const {
    seats: baseSeats,
    isLive,
    taxTokenDecimals,
    taxTokenSymbol,
    taxTokenPriceUsd,
  } = useLobbySeats({
    totalSeats: gridSize * gridSize,
    gridId: boardId,
    gridPage,
  });

  // Per-seat state. These were previously hoisted in `LobbyClient`,
  // which made cross-board navigations bleed (board A's pending buyout
  // animation ran on board B's seat sharing the same seatIndex). Keeping
  // them per-slot guarantees a freshly-mounted slot starts empty and a
  // slot that's about to unmount can't leak its state into the
  // incoming one.
  const [tileAnimStates, setTileAnimStates] = useState<
    Record<number, TileAnimState>
  >({});
  /** Pins tiles at their current board ranks until cinematics finish — keeps placement hooks stable mid-buyout/claim. */
  const [sortLayoutPins, setSortLayoutPins] = useState<Map<number, number>>(new Map());
  const [holderOverrides, setHolderOverrides] = useState<
    Record<number, TileHolderSnapshot>
  >({});

  /**
   * Optimistic-holder overlay applied only to the canvas's view of
   * `seats`. The HUD (`OwnedTilesBar` etc.) reads the unmerged base
   * data from `LobbyClient` — see plan §"Per-board state isolation".
   */
  const seats = useMemo(() => {
    if (Object.keys(holderOverrides).length === 0) return baseSeats;
    return baseSeats.map((s) => {
      const override = holderOverrides[s.seatIndex];
      if (!override) return s;
      return {
        ...s,
        holder: override.address ?? s.holder,
        holderAvatar:
          override.avatarUrl !== undefined ? override.avatarUrl : s.holderAvatar,
        holderLevel:
          override.level !== undefined ? override.level : s.holderLevel,
      };
    });
  }, [baseSeats, holderOverrides]);

  const holderPlinths = useHolderPlinths(seats);

  // Choose 2D vs 3D from THIS slot's seat count, not the URL's. A
  // future board big enough to force 2D can crossfade against a 3D
  // slot of the previous board — both are just opacity layers in
  // the same wrapper, so the mode mismatch is transparent.
  const effectiveTwoD = shouldForceTwoD(seats.length, graphics.twoD);

  // ─── Tutorial bridge ───────────────────────────────────────────────
  // Tutorial mode owns the entire seat surface from the script. It
  // injects buyouts / claims via `setSingleTileAnim` (the demo
  // choreography is bypassed). The bridge installs while this slot
  // is the active URL board so the script always targets the
  // currently-visible canvas.
  const tutorialActiveRef = useRef(!!tutorial);
  tutorialActiveRef.current = !!tutorial;

  const seatsRef = useRef(seats);
  seatsRef.current = seats;
  const tileAnimStatesRef = useRef(tileAnimStates);
  tileAnimStatesRef.current = tileAnimStates;
  const sortModeRef = useRef(sortMode);
  sortModeRef.current = sortMode;
  const sortLayoutPinsRef = useRef(sortLayoutPins);
  sortLayoutPinsRef.current = sortLayoutPins;

  /**
   * Counts buyout/claim cinematics currently playing on this slot. The
   * board-wide sort pin (`sortLayoutPins` keyed by seat → rank) covers
   * **every visible seat** while the counter is > 0, so neighbours of
   * the cinematic seat stay frozen in their pre-buyout positions for
   * the full ~1.7 s shake-shatter-regrow window. Decremented when each
   * cinematic's cleanup timer fires; once it reaches 0 the pins clear
   * in one beat and `usePlacementTransitions` slides every shifted
   * tile to its new sorted slot in lockstep — the satisfying "now
   * everything settles" beat the user wants instead of the previous
   * "tiles move and animate at the same time" mush.
   *
   * Held in a ref because we need to read+mutate it from inside event
   * handlers without re-rendering the slot every time a counter ticks.
   */
  const activeAnimCountRef = useRef(0);

  // ─── Per-seat animation API ───────────────────────────────────────
  /**
   * Apply a single tile-anim state and schedule its cleanup.
   *
   * Re-implemented here (instead of imported from LobbyClient) so
   * the timers, `setHolderOverrides` writes, and the buyout-confirm
   * chime all live in the slot's render tree — when the slot
   * unmounts, every pending timer goes with it via React's effect-
   * cleanup chain.
   */
  const setSingleTileAnim = useCallback(
    (seatIndex: number, animState: TileAnimState) => {
      const isCinematic =
        animState.kind === 'buyout' || animState.kind === 'claim';

      if (isCinematic) {
        // Snapshot the *entire* sorted board on the first concurrent
        // cinematic and pin every seat at its current rank for the
        // duration. The previous behaviour pinned only the bought/
        // claimed seat — its neighbours were free to slide as
        // `selfAssessedPrice` updates rippled through the sort,
        // producing the user-reported "tiles move and animate at
        // the same time".
        //
        // Pass the existing `sortLayoutPins` into the sort so any
        // pins from an earlier overlapping cinematic carry forward
        // — without this the new pin set could re-order seats that
        // an in-flight buyout was already keeping put, snapping
        // them mid-cinematic.
        if (activeAnimCountRef.current === 0) {
          const sorted = sortedSeatsForBoard(
            seatsRef.current,
            sortModeRef.current,
            sortLayoutPinsRef.current,
          );
          const fullPins = new Map<number, number>();
          for (let rank = 0; rank < sorted.length; rank++) {
            const seat = sorted[rank];
            if (!seat) continue;
            fullPins.set(seat.seatIndex, rank);
          }
          setSortLayoutPins(fullPins);
        }
        activeAnimCountRef.current += 1;
      }

      setTileAnimStates((prev) => ({ ...prev, [seatIndex]: animState }));
      if (isCinematic) {
        const cleanupAfter =
          animState.kind === 'claim'
            ? CLAIM_TOTAL_MS + 100
            : BUYOUT_TOTAL_MS + 100;
        if (animState.kind === 'buyout') {
          window.setTimeout(() => {
            hqPlayBuyoutConfirm();
          }, BUYOUT_TILE_VISIBLE_AT);
        }
        const newHolder = animState.newHolder;
        window.setTimeout(() => {
          // Decrement first; only the *last* cinematic's cleanup
          // releases the board-wide pin. While other cinematics
          // are still in flight the board stays frozen so they
          // can finish their own shake/shatter in place too.
          activeAnimCountRef.current = Math.max(
            0,
            activeAnimCountRef.current - 1,
          );
          if (activeAnimCountRef.current === 0) {
            setSortLayoutPins(new Map());
          }
          if (newHolder) {
            setHolderOverrides((prev) => ({
              ...prev,
              [seatIndex]: newHolder,
            }));
          }
          setTileAnimStates((p) => {
            if (p[seatIndex] !== animState) return p;
            const rest = { ...p };
            delete rest[seatIndex];
            return rest;
          });
        }, cleanupAfter);
      } else if (
        animState.kind === 'waterfall' ||
        animState.kind === 'dutchAuction'
      ) {
        const cleanupAfter = animState.durationMs + 150;
        window.setTimeout(() => {
          setTileAnimStates((p) => {
            if (p[seatIndex] !== animState) return p;
            const rest = { ...p };
            delete rest[seatIndex];
            return rest;
          });
        }, cleanupAfter);
      }
    },
    [],
  );

  // Cooldown guard for the heavy shatter SFX — a buyout flurry
  // collapses onto a single shatter while every tile still gets its
  // own cinematic. Per-slot ref so two slots can't share a stale
  // timestamp across a board switch.
  const lastShatterAtRef = useRef(0);
  const playBuyoutAnimation = useCallback(
    (
      seatIndex: number,
      previousHolder: TileHolderSnapshot,
      newHolder: TileHolderSnapshot,
    ) => {
      setSingleTileAnim(seatIndex, {
        kind: 'buyout',
        startedAt: performance.now(),
        previousHolder,
        newHolder,
      });
      const now = performance.now();
      if (now - lastShatterAtRef.current > 1000) {
        lastShatterAtRef.current = now;
        ensureHqAudioReady();
        hqPlayBuyout();
      }
    },
    [setSingleTileAnim],
  );

  const playClaimAnimation = useCallback(
    (seatIndex: number, newHolder: TileHolderSnapshot) => {
      setSingleTileAnim(seatIndex, {
        kind: 'claim',
        startedAt: performance.now(),
        newHolder,
      });
      ensureHqAudioReady();
      hqPlayClaim();
    },
    [setSingleTileAnim],
  );

  // Latest seats / tileAnimStates available to the dev `triggerTileAnim`
  // closure without re-binding it on every state tick. The animation
  // demo panel re-installs the API into the stage context only when
  // its identity changes, so we want a stable reference.
  /** Demo / dev-only batch trigger. Routed through the stage so the
   *  States panel always lands on the active board's slot, even when
   *  a transition is in flight. */
  const triggerTileAnim = useCallback(
    (kind: TileAnimKind, count: number = 1) => {
      if (kind === 'reset') {
        setTileAnimStates({});
        setHolderOverrides({});
        setSortLayoutPins(new Map());
        // Hard-reset the cinematic counter so the next dev-panel
        // trigger establishes a fresh full-board pin from scratch
        // instead of carrying a stale increment forward.
        activeAnimCountRef.current = 0;
        return;
      }
      const baseState = buildAnimState(kind);
      if (!baseState) return;

      const currentSeats = seatsRef.current;
      const currentAnimStates = tileAnimStatesRef.current;
      const heldIndices = currentSeats
        .filter((s) => s.holder && !s.isForfeited)
        .map((s) => s.seatIndex);

      const candidates = heldIndices.filter(
        (i) => !currentAnimStates[i] || currentAnimStates[i].kind === 'idle',
      );
      const pool = candidates.length > 0 ? candidates : heldIndices;
      if (pool.length === 0) return;

      const poolCopy = [...pool];
      const targets: number[] = [];
      const n = Math.min(Math.max(1, count), poolCopy.length);
      for (let i = 0; i < n; i++) {
        const pickIdx = Math.floor(Math.random() * poolCopy.length);
        targets.push(poolCopy[pickIdx]);
        poolCopy.splice(pickIdx, 1);
      }

      const assignments = targets.map((index) => {
        const targetSeat = currentSeats[index];
        let state: TileAnimState = baseState;
        let newHolderToPersist: TileHolderSnapshot | undefined;
        if (baseState.kind === 'buyout' || baseState.kind === 'claim') {
          const previousHolder: TileHolderSnapshot | undefined = targetSeat
            ? {
                address: targetSeat.holder,
                avatarUrl: targetSeat.holderAvatar ?? null,
                level: targetSeat.holderLevel ?? 0,
              }
            : undefined;
          const newHolder = pickDifferentHolder(currentSeats, targetSeat);
          newHolderToPersist = newHolder;
          if (baseState.kind === 'buyout') {
            state = { ...baseState, previousHolder, newHolder };
          } else {
            state = { ...baseState, newHolder };
          }
        }
        return { index, state, newHolderToPersist };
      });

      // Pin the entire sorted board if any of this batch is a
      // buyout/claim cinematic and we're not already inside an
      // active cinematic window. Mirrors `setSingleTileAnim`'s
      // pin/unpin policy so dev-panel batch triggers behave the
      // same as live-event-driven ones — every tile in the batch
      // shakes/shatters in place, and the resort beat fires once
      // every cinematic in the batch has finished.
      const cinematicAssignments = assignments.filter(
        (a) => a.state.kind === 'buyout' || a.state.kind === 'claim',
      );
      if (cinematicAssignments.length > 0 && activeAnimCountRef.current === 0) {
        const sorted = sortedSeatsForBoard(
          seatsRef.current,
          sortModeRef.current,
          sortLayoutPinsRef.current,
        );
        const fullPins = new Map<number, number>();
        for (let rank = 0; rank < sorted.length; rank++) {
          const seat = sorted[rank];
          if (!seat) continue;
          fullPins.set(seat.seatIndex, rank);
        }
        setSortLayoutPins(fullPins);
      }
      activeAnimCountRef.current += cinematicAssignments.length;

      setTileAnimStates((prev) => {
        const next = { ...prev };
        for (const a of assignments) next[a.index] = a.state;
        return next;
      });

      for (const a of assignments) {
        if (a.state.kind === 'buyout' || a.state.kind === 'claim') {
          const cleanupAfter =
            a.state.kind === 'claim'
              ? CLAIM_TOTAL_MS + 100
              : BUYOUT_TOTAL_MS + 100;
          if (a.state.kind === 'buyout') {
            window.setTimeout(() => {
              hqPlayBuyoutConfirm();
            }, BUYOUT_TILE_VISIBLE_AT);
          }
          window.setTimeout(() => {
            activeAnimCountRef.current = Math.max(
              0,
              activeAnimCountRef.current - 1,
            );
            if (activeAnimCountRef.current === 0) {
              setSortLayoutPins(new Map());
            }
            if (a.newHolderToPersist) {
              setHolderOverrides((prev) => ({
                ...prev,
                [a.index]: a.newHolderToPersist!,
              }));
            }
            setTileAnimStates((p) => {
              if (p[a.index] !== a.state) return p;
              const rest = { ...p };
              delete rest[a.index];
              return rest;
            });
          }, cleanupAfter);
        } else if (
          a.state.kind === 'waterfall' ||
          a.state.kind === 'dutchAuction'
        ) {
          const cleanupAfter = a.state.durationMs + 150;
          window.setTimeout(() => {
            setTileAnimStates((p) => {
              if (p[a.index] !== a.state) return p;
              const rest = { ...p };
              delete rest[a.index];
              return rest;
            });
          }, cleanupAfter);
        }
      }
    },
    [],
  );

  // ─── Live FORFEITED / BOUGHT pipeline ─────────────────────────────
  /**
   * Forfeit baseline / live event listener — moved from LobbyClient.
   * Each slot baselines independently from the moment it's mounted,
   * so the previous board's `seenForfeitsRef` can never cause a
   * historical event to replay on a freshly-mounted slot. The
   * `baselined` flag flips true once the data has actually loaded
   * for THIS slot's boardId.
   */
  const seenForfeitsRef = useRef<{
    ids: Set<string>;
    baselined: boolean;
  }>({ ids: new Set(), baselined: false });

  useEffect(() => {
    if (tutorialActiveRef.current) return;
    const cur = seenForfeitsRef.current;
    if (!cur.baselined) {
      for (const evt of gridPage.recentEvents) cur.ids.add(evt.id);
      if (!gridPage.gridLoading) cur.baselined = true;
      return;
    }

    for (const evt of gridPage.recentEvents) {
      if (cur.ids.has(evt.id)) continue;
      if (!evt.seatId) continue;
      const parts = evt.seatId.split('-');
      const seatIdx = parseInt(parts[parts.length - 1], 10);
      if (!Number.isFinite(seatIdx)) continue;

      const eventType = evt.eventType as string;
      const actor = (evt.user ?? '').toLowerCase();
      const isSelf =
        !!viewerAddress && actor === viewerAddress.toLowerCase();

      cur.ids.add(evt.id);

      if (eventType === 'FORFEITED') {
        const v22 = isV22Grid(gridPage.grid?.managerAddress);
        const fd =
          gridPage.grid?.forfeitureDuration &&
          gridPage.grid.forfeitureDuration > 0
            ? gridPage.grid.forfeitureDuration
            : V2_CONSTANTS.V22_DEFAULT_FORFEITURE_DURATION;
        setSingleTileAnim(seatIdx, { kind: 'forfeit' });
        window.setTimeout(() => {
          setSingleTileAnim(
            seatIdx,
            v22
              ? {
                  kind: 'dutchAuction',
                  startedAt: performance.now(),
                  durationMs: fd * 1000,
                }
              : {
                  kind: 'waterfall',
                  startedAt: performance.now(),
                  durationMs: V2_CONSTANTS.ANTI_SNIPE_WINDOW * 1000,
                },
          );
        }, FORFEIT_DRAMATIC_MS);
        continue;
      }

      if (isSelf) continue;

      if (
        eventType === 'BOUGHT_OUT' ||
        eventType === 'BOUGHT_FROM_CREATOR'
      ) {
        playBuyoutAnimation(
          seatIdx,
          {
            address: evt.oldHolder ?? null,
            avatarUrl: evt.oldHolderIdentity?.avatarUrl,
            level: evt.oldHolderIdentity?.level,
          },
          {
            address: evt.newHolder ?? evt.user ?? null,
            avatarUrl: evt.userIdentity?.avatarUrl,
            level: evt.userIdentity?.level,
          },
        );
        continue;
      }

      if (eventType === 'BOUGHT_VACANT' || eventType === 'CLAIMED') {
        playClaimAnimation(seatIdx, {
          address: evt.newHolder ?? evt.user ?? null,
          avatarUrl: evt.userIdentity?.avatarUrl,
          level: evt.userIdentity?.level,
        });
        continue;
      }

      if (eventType === 'BOUGHT') {
        // Legacy V1-shaped event arriving via the live SSE feed's
        // `mapV2GridEventTypeToV1` collapse: V22 emits both
        // `BOUGHT_FROM_CREATOR` and `BOUGHT_OUT` as `BOUGHT` here, and
        // the V22 indexer drops `oldHolder_id` for lazy-materialised
        // creator buys (`isVacant === true` short-circuits the field).
        // The previous heuristic (`!evt.oldHolder ⇒ claim`) therefore
        // mis-routed every primary-mint creator buyout into the claim
        // cinematic for *other* viewers — locally the buyer's drawer
        // already invoked `playBuyoutAnimation` directly, but every
        // remote client saw the green claim ring instead of the
        // shatter. The user's brief: paid acquisitions (creator +
        // secondary) always read as buyout; the green claim ring is
        // reserved exclusively for the dedicated `CLAIMED` /
        // `BOUGHT_VACANT` (Dutch reclaim of a forfeited seat) path
        // handled in the branch above.
        playBuyoutAnimation(
          seatIdx,
          {
            address: evt.oldHolder ?? null,
            avatarUrl: evt.oldHolderIdentity?.avatarUrl,
            level: evt.oldHolderIdentity?.level,
          },
          {
            address: evt.newHolder ?? evt.user ?? null,
            avatarUrl: evt.userIdentity?.avatarUrl,
            level: evt.userIdentity?.level,
          },
        );
        continue;
      }
    }
  }, [
    gridPage.recentEvents,
    gridPage.gridLoading,
    gridPage.grid?.managerAddress,
    gridPage.grid?.forfeitureDuration,
    setSingleTileAnim,
    viewerAddress,
    playBuyoutAnimation,
    playClaimAnimation,
  ]);

  // ─── Demo seeding (mock boards only) ──────────────────────────────
  const seededIndices = useMemo(() => {
    if (isLive) return null;
    if (tutorial) return null;
    const held: number[] = [];
    for (const s of seats) {
      if (s.holder && !s.isForfeited) held.push(s.seatIndex);
      if (held.length >= 4) break;
    }
    return {
      forfeit: held[0] ?? 34,
      waterfall: held[1] ?? 47,
    };
  }, [seats, isLive, tutorial]);

  const seededRef = useRef<{ forfeit: number | null; waterfall: number | null }>({
    forfeit: null,
    waterfall: null,
  });

  useEffect(() => {
    if (!seededIndices) {
      const prevSeeded = seededRef.current;
      if (prevSeeded.forfeit === null && prevSeeded.waterfall === null) return;
      setTileAnimStates((prev) => {
        const next = { ...prev };
        if (
          prevSeeded.forfeit !== null &&
          next[prevSeeded.forfeit]?.kind === 'forfeit'
        ) {
          delete next[prevSeeded.forfeit];
        }
        if (
          prevSeeded.waterfall !== null &&
          (next[prevSeeded.waterfall]?.kind === 'waterfall' ||
            next[prevSeeded.waterfall]?.kind === 'dutchAuction')
        ) {
          delete next[prevSeeded.waterfall];
        }
        return next;
      });
      seededRef.current = { forfeit: null, waterfall: null };
      return;
    }
    const { forfeit, waterfall } = seededIndices;
    const startedAt = performance.now();
    seededRef.current = { forfeit, waterfall };
    setTileAnimStates((prev) => {
      const next: Record<number, TileAnimState> = { ...prev };
      if (!next[forfeit]) {
        next[forfeit] = { kind: 'forfeit' };
      }
      const existing = next[waterfall];
      if (!existing || existing.kind !== 'waterfall') {
        next[waterfall] = {
          kind: 'waterfall',
          startedAt,
          durationMs: 12_000,
        };
      }
      return next;
    });
  }, [seededIndices]);

  // ─── Stage registration ───────────────────────────────────────────
  // Install the per-slot animation API into the stage context whenever
  // this slot owns the URL-active board. The stage routes drawer +
  // tutorial dispatches to whichever slot most recently registered;
  // because exactly one slot can have `isActiveTarget=true` at a
  // time, the routing is unambiguous.
  useEffect(() => {
    if (!isActiveTarget) return;
    return stage.__registerActiveSlot({
      playBuyoutAnimation,
      playClaimAnimation,
      triggerTileAnim,
      setSingleTileAnim,
    });
  }, [
    isActiveTarget,
    stage,
    playBuyoutAnimation,
    playClaimAnimation,
    triggerTileAnim,
    setSingleTileAnim,
  ]);

  // ─── Readiness gate ───────────────────────────────────────────────
  /**
   * Three preconditions must all be satisfied before the slot
   * signals readiness to the stage:
   *   1. `gridPage.gridLoading=false` — seats + metadata resolved
   *      (so the canvas isn't about to re-render with newly arrived
   *      data mid-fade).
   *   2. The forfeit baseline has flipped — the slot has ingested
   *      every currently-known event id, so the live listener
   *      above will only react to genuinely new events from now
   *      on. This is what stops "buyouts from hours ago replay
   *      on every board switch".
   *   3. The canvas has fired its `onReady` callback (after R3F's
   *      `onCreated` for 3D, or post-mount for 2D) AND one extra
   *      animation frame has elapsed so React's commit + the
   *      browser's paint have completed. Without the rAF the
   *      crossfade can begin while the canvas is still painting
   *      the first frame, producing a brief flash of empty/
   *      half-painted board on screen.
   *
   * Tutorial mode bypasses the gate entirely — the script doesn't
   * fetch live data, so waiting on `gridLoading` would deadlock
   * the readiness signal.
   */
  const [canvasFirstFramePainted, setCanvasFirstFramePainted] = useState(false);
  const [readinessFired, setReadinessFired] = useState(false);

  // Refs guard against the canvas firing `onReady` more than once
  // (e.g. R3F's `onCreated` re-firing under HMR or a defensive 2D
  // re-mount) so the rAF below only ever schedules a single
  // commit. `cancelled` lives on the ref too because the slot can
  // unmount before the rAF resolves — we must not call setState
  // on an unmounted slot.
  const canvasReadyHandledRef = useRef(false);
  const canvasReadyRafRef = useRef(0);
  const handleCanvasReady = useCallback(() => {
    if (canvasReadyHandledRef.current) return;
    canvasReadyHandledRef.current = true;
    recordBootPhase('board.canvas_surface_ready', {
      boardIdShort: boardId.length > 12 ? boardId.slice(0, 12) : boardId,
      twoD: effectiveTwoD,
      graphicsLevel: graphics.level,
    });
    canvasReadyRafRef.current = requestAnimationFrame(() => {
      canvasReadyRafRef.current = 0;
      setCanvasFirstFramePainted(true);
    });
  }, [boardId, effectiveTwoD, graphics.level]);
  useEffect(() => {
    return () => {
      if (canvasReadyRafRef.current) {
        cancelAnimationFrame(canvasReadyRafRef.current);
      }
    };
  }, []);

  // Track whether the forfeit baseline has flipped without subscribing
  // to the ref directly (refs don't trigger re-renders). The effect
  // observes the same `gridLoading` signal that flips `baselined`.
  const baselinedFlipped =
    !gridPage.gridLoading || seenForfeitsRef.current.baselined;

  useEffect(() => {
    if (readinessFired) return;
    if (!onReady) return;
    if (bypassReadinessGate) {
      setReadinessFired(true);
      onReady();
      return;
    }
    const dataReady = !gridPage.gridLoading;
    if (!dataReady) return;
    if (!baselinedFlipped) return;
    if (!canvasFirstFramePainted) return;
    setReadinessFired(true);
    onReady();
  }, [
    readinessFired,
    onReady,
    bypassReadinessGate,
    gridPage.gridLoading,
    baselinedFlipped,
    canvasFirstFramePainted,
  ]);

  // ─── Render ───────────────────────────────────────────────────────
  // `interactionsLocked` covers two cases:
  //   • the slot is mid-fade (incoming or outgoing), so a click
  //     here would target a board that's about to appear / vanish;
  //   • the slot's data is still loading, which preserves today's
  //     behaviour for the active slot before its first paint.
  const interactionsLocked =
    phase === 'incoming-loading' ||
    phase === 'incoming-fading-in' ||
    phase === 'outgoing-fading-out' ||
    (isLive && gridPage.gridLoading);

  return (
    <div
      className="lobby-board-stage-slot absolute inset-0"
      data-stage-phase={phase}
      // Only the `current` slot can take pointer events — the
      // outgoing slot would otherwise eat clicks during its fade
      // out, and the incoming slot mustn't intercept clicks for
      // tiles it hasn't had a chance to validate yet.
      style={{
        pointerEvents: phase === 'current' ? 'auto' : 'none',
      }}
      aria-hidden={phase !== 'current'}
    >
      {effectiveTwoD ? (
        <BoardCanvas2D
          seats={seats}
          gridSize={gridSize}
          boardPlinth={boardPlinth}
          // Forward the same skybox the 3D path receives so the 2D
          // backdrop matches the creator's saved theme (`'desert'`,
          // `'aurora'`, …). Without this prop Lite-mode users saw a
          // flat near-black void on every board regardless of theme,
          // even though the launch-wizard pickers and lobby cards all
          // honoured the choice. See `theming/skyboxes/skybox-2d.ts`
          // for the per-theme palette derivation.
          skybox={skybox}
          lodPolicy={graphics.lodPolicy}
          demoOwnerIndex={null}
          onTileClick={onTileClick}
          selectedIndices={selectedIndices}
          bulkMode={bulkMode}
          tileAnimStates={tileAnimStates}
          priceDecimals={taxTokenDecimals}
          priceTokenPriceUsd={taxTokenPriceUsd}
          priceTokenSymbol={taxTokenSymbol}
          devRandomPedestals={!!devRandomPedestals}
          holderPlinths={holderPlinths}
          interactionsLocked={interactionsLocked}
          sortMode={sortMode}
          sortLayoutPins={sortLayoutPins}
          seatTooltip={seatTooltip}
          stickyTooltipIndex={stickyTooltipIndex}
          onClearStickyTooltip={onClearStickyTooltip}
          isMobile={isMobile}
          onReady={handleCanvasReady}
        />
      ) : (
        // Each slot gets its OWN error boundary scoped to its own
        // mount — never keyed on a shared `activeBoardId` like the
        // previous LobbyClient. The boundary's `hasError` state
        // unwinds with the slot when the stage unmounts it after a
        // crossfade, so a one-off shader compile failure never
        // pins the user to 2D for a future board.
        <CanvasErrorBoundary
          fallback={
            <BoardCanvas2D
              seats={seats}
              gridSize={gridSize}
              boardPlinth={boardPlinth}
              // Same skybox forwarding as the primary 2D path above —
              // when WebGL crashes mid-session and we drop into the
              // boundary's fallback, the user should still see their
              // theme rather than a sudden black void.
              skybox={skybox}
              lodPolicy={graphics.lodPolicy}
              demoOwnerIndex={null}
              onTileClick={onTileClick}
              selectedIndices={selectedIndices}
              bulkMode={bulkMode}
              tileAnimStates={tileAnimStates}
              priceDecimals={taxTokenDecimals}
              priceTokenPriceUsd={taxTokenPriceUsd}
              priceTokenSymbol={taxTokenSymbol}
              devRandomPedestals={!!devRandomPedestals}
              holderPlinths={holderPlinths}
            interactionsLocked={interactionsLocked}
            sortMode={sortMode}
            sortLayoutPins={sortLayoutPins}
            seatTooltip={seatTooltip}
            stickyTooltipIndex={stickyTooltipIndex}
              onClearStickyTooltip={onClearStickyTooltip}
              isMobile={isMobile}
              onReady={handleCanvasReady}
            />
          }
        >
          <BoardCanvas3D
            seats={seats}
            gridSize={gridSize}
            skybox={skybox}
            boardPlinth={boardPlinth}
            isMobile={isMobile}
            reducedMotion={reducedMotion}
            demoOwnerIndex={null}
            onTileClick={onTileClick}
            selectedIndices={selectedIndices}
            bulkMode={bulkMode}
            graphics={graphics}
            tileAnimStates={tileAnimStates}
            priceDecimals={taxTokenDecimals}
            priceTokenPriceUsd={taxTokenPriceUsd}
            priceTokenSymbol={taxTokenSymbol}
            devRandomPedestals={!!devRandomPedestals}
            holderPlinths={holderPlinths}
            interactionsLocked={interactionsLocked}
            sortMode={sortMode}
            sortLayoutPins={sortLayoutPins}
            seatTooltip={seatTooltip}
            stickyTooltipIndex={stickyTooltipIndex}
            onClearStickyTooltip={onClearStickyTooltip}
            drawerOverlayOpen={!!drawerOverlayOpen}
            onReady={handleCanvasReady}
          />
        </CanvasErrorBoundary>
      )}
    </div>
  );
}

/**
 * Demo helper for `triggerTileAnim` — picks a holder different from
 * the target seat's so the buyout/claim cinematic visibly swaps
 * avatars even on a mock board where seat data is static. Identical
 * behaviour to the previous `LobbyClient` helper of the same name;
 * inlined here so the slot doesn't import from `LobbyClient`.
 */
function pickDifferentHolder(
  seats: SeatData[],
  currentSeat: SeatData | undefined,
): TileHolderSnapshot | undefined {
  const currentHolder = currentSeat?.holder ?? null;
  const alternatives = seats.filter(
    (s) => !!s.holder && !s.isForfeited && s.holder !== currentHolder,
  );
  if (alternatives.length > 0) {
    const pick = alternatives[Math.floor(Math.random() * alternatives.length)];
    return {
      address: pick.holder,
      avatarUrl: pick.holderAvatar ?? null,
      level: pick.holderLevel ?? 0,
    };
  }
  const salt = Math.floor(Math.random() * 0xffffffff)
    .toString(16)
    .padStart(8, '0');
  const hex = `0x${salt.repeat(5)}`.slice(0, 42);
  return { address: hex, avatarUrl: null, level: 1 };
}
